import React, { useEffect, useState, useMemo } from "react";
import { Tournament, TournamentEvent, Season } from "../../types"; 
import { TournamentEventCard } from "../../components/common/TournamentEventCard";
import { TournamentCard } from "../../components/common/TournamentCard";
import { ModalSelectSeason } from "../../components/common/ModalSelectSeason";
import { 
  getCurrentSeason,
  createTournamentEvent, 
  getListTournamentEvents, 
  getListTournaments 
} from "../../api";
import { Button } from "../../components/common/Button";
import { useAuth } from "../../contexts/AuthContext";

export const TournamentsPage: React.FC = () => {
  const { user } = useAuth();
  const [currentSeason, setCurrentSeason] = useState<Season | null>(null);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [events, setEvents] = useState<TournamentEvent[]>([]);
  const [seasonModal, setSeasonModal] = useState(false);
  const [showCreateEvents, setShowCreateEvents] = useState(false);
  
  // set dei dati (currentSeason, seasons, tournaments) al moutn
  useEffect(() => {
    const loadData = async () => {
      setShowCreateEvents(!!(user && (user.can_manage_all || user.can_manage_content)));
      // Stagione attiva
      setCurrentSeason(await getCurrentSeason());
    };
    loadData();
  }, [user]);
  
  // set dei dati (currentSeason, seasons, tournaments) al moutn
  useEffect(() => {
    const loadData = async () => {
      if (currentSeason) {
        setTournaments(await getListTournaments({}));
      }
    };
    loadData();
  }, [currentSeason]);

  
  const getAndSetEvents = async () => {
    if (!currentSeason) return;
    // Eventi
    const data_events = await getListTournamentEvents({ seasonId: currentSeason.id });
    const today = new Date().toISOString().split("T")[0];
    const isUpcoming = (d?: string) => (d || "") >= today;
    const sortedData = data_events.sort((a, b) => {
      const aUpcoming = isUpcoming(a.date);
      const bUpcoming = isUpcoming(b.date);
      if (aUpcoming !== bUpcoming) return aUpcoming ? -1 : 1; // prima i futuri
      const ad = a.date || "";
      const bd = b.date || "";
      // ordine decrescente per data se passati, crescente se futuri
      return aUpcoming ? ad.localeCompare(bd) : bd.localeCompare(ad);
    });
    setEvents(sortedData);
  };

  // Fetch tornei + eventi per la stagione selezionata
  useEffect(() => {
    getAndSetEvents();
  }, [currentSeason]);

  // Funzione per creare un nuovo evento (dummy -> chiama API POST)
  const handleCreateEvent = async (tournament: Tournament) => {
    if (!currentSeason) return;

    await createTournamentEvent({
      tournament_id: tournament.id,
      season_id: currentSeason.id,
      state: "da_programmare",
      tournament_points: currentSeason.default_tournament_points,
      results: [],
    })

    // ricarico gli eventi
    getAndSetEvents();
  };

  const tournamentsWithEvent = events.map((e) => e.tournament.id);
  const tournamentsWithoutEvent = useMemo(() => {
    return tournaments.filter((t) => !tournamentsWithEvent.includes(t.id));
  }, [tournaments, tournamentsWithEvent]); 

  return (
    <div className="p-4-y-6">
      {/* Title e selezione stagione */}
      <div className="flex justify-between items-center">
        <h1 className="text-xl font-bold">Tornei</h1>
        <Button
          variant="primary"
          onClick={() => setSeasonModal(true)}
        >
          {currentSeason?.name || "Seleziona"}
        </Button>
      </div>

      {/* Modal per selezione stagione */}
      { seasonModal && ( 
        <ModalSelectSeason 
          currentSeason={currentSeason!} 
          onSelect={(s) => { 
            setCurrentSeason(s);
            setSeasonModal(false);
          }} 
          onClose={() => setSeasonModal(false)} /> 
        )
      } 

      {/* Sezione eventi già programmati */}
      <section>
        <h2 className="text-lg font-semibold my-4">
          Tornei programmati ({events.length})
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {events.map((e) => (
            <TournamentEventCard key={e.id} tournamentEvent={e} />
          ))}
        </div>
      </section>

      {/* Sezione tornei senza eventi (solo per chi ha permessi) */}
      {showCreateEvents && (
        <section>
          <h2 className="text-lg font-semibold my-4">
            Tornei senza evento ({tournamentsWithoutEvent.length})
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {tournamentsWithoutEvent.map((t, index) => (
              <TournamentCard
                key={"tournament-" + index}
                tournament={t}
                onCreateEvent={(t) => handleCreateEvent(t)}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
};
